/**
 * useCustomerCardCdlAnalytics
 *
 * Maps the customer-card activity's host-agnostic analytics events to CDL tags and dispatches them.
 * The activity only knows *when* and *which* neutral event fired (see CUSTOMER_CARD_ANALYTICS); this
 * hook owns turning that into an actual Pixall event, the same split already used for
 * `onAnalyticsEvent` elsewhere. Composes with the caller's own handler (e.g. useSCICustomerCard's
 * CREDIT_CONSENT_CLICKED) so both concerns run off the single `onAnalyticsEvent` prop
 * `UnifiedCustomerCard` exposes.
 */
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
    CUSTOMER_CARD_ANALYTICS,
    CUSTOMER_TABS,
    type CustomerCardAnalyticsEvent,
    type OnAnalyticsEvent
} from '@makemydeal/dr-activities-customer-card';
import { SCI_CDL_TAGS } from '@makemydeal/dr-sales-checkin-types';
import { CDL_V3_EVENT } from '@makemydeal/dr-shared-types';

const roleLabel = (customerRole: unknown) => (customerRole === 'coBuyer' ? 'Co-Buyer' : 'Buyer');

const buildCdlEventForCustomerCardAnalytics = (eventName: CustomerCardAnalyticsEvent, data?: Record<string, unknown>) => {
    const role = roleLabel(data?.customerRole);
    const subTab = data?.subTab;
    switch (eventName) {
        case CUSTOMER_CARD_ANALYTICS.CLICKED_ADD_BILLING_ADDRESS:
            return SCI_CDL_TAGS['dc-sci-089'](role);
        case CUSTOMER_CARD_ANALYTICS.CLICKED_HOUSING_EMPLOYMENT_ACCORDION:
            return SCI_CDL_TAGS['dc-sci-090'](role);
        case CUSTOMER_CARD_ANALYTICS.CLICKED_ADD_PREVIOUS_EMPLOYMENT:
            return SCI_CDL_TAGS['dc-sci-091'](role);
        case CUSTOMER_CARD_ANALYTICS.CLICKED_ADD_ADDITIONAL_INCOME:
            return SCI_CDL_TAGS['dc-sci-092'](role);
        case CUSTOMER_CARD_ANALYTICS.CLICKED_ADD_PREVIOUS_ADDRESS:
            return SCI_CDL_TAGS['dc-sci-093'](role);
        case CUSTOMER_CARD_ANALYTICS.CLICKED_CUSTOMER_SUB_TAB:
            // Customer Details has exactly two tagged tabs; anything else stays silent.
            if (subTab === CUSTOMER_TABS.BUYER_DETAILS) return SCI_CDL_TAGS['dc-sci-067']();
            if (subTab === CUSTOMER_TABS.INSURANCE) return SCI_CDL_TAGS['dc-sci-068']();
            return undefined;
        default:
            // CLICKED_SHOW_CUSTOMER_SPD and the spread-in INSURANCE_FORM_ANALYTICS events aren't
            // CDL-tagged today — not this feature's scope.
            return undefined;
    }
};

export const useCustomerCardCdlAnalytics = (onAnalyticsEvent: OnAnalyticsEvent): OnAnalyticsEvent => {
    const dispatch = useDispatch();

    return useCallback(
        (eventName: CustomerCardAnalyticsEvent, data?: Record<string, unknown>) => {
            onAnalyticsEvent(eventName, data);
            const event = buildCdlEventForCustomerCardAnalytics(eventName, data);
            if (event) {
                dispatch({ type: CDL_V3_EVENT, payload: event });
            }
        },
        [dispatch, onAnalyticsEvent]
    );
};
